import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Unlock, Check, Printer, Link2, FileSignature, Minus } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Reveal, MaskedLines, EASE } from "@/components/Motion";
import { WinnerBadges } from "@/components/WinnerBadge";
import { TierBadge, tierStyle, DeadlineChip } from "@/components/Tiers";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { SampleBadge } from "@/components/SampleBadge";

const Fact = ({ label, value, testid }) => value ? (
  <div className="border-t border-[#2A1240]/10 pt-4" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">{label}</p>
    <p className="mt-2 font-display text-2xl text-[#2A1240]">{value}</p>
  </div>
) : null;

function NdaGate({ b, reload }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sig, setSig] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const n = b.nda;
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="relative overflow-hidden rounded-3xl border border-[#E0218A]/30 bg-[#FFFFFF] p-8 sm:p-12" data-testid="nda-gate">
      <div className="absolute inset-0 opacity-30 blur-md pointer-events-none select-none" aria-hidden="true">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-6">{[1, 2, 3, 4].map((k) => <div key={k} className="h-48 rounded-2xl bg-[#2A1240]/5" />)}</div>
      </div>
      <div className="relative max-w-2xl">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-[#E0218A] text-white"><Lock className="h-5 w-5" /></span>
        <h3 className="mt-6 font-display text-3xl sm:text-4xl text-[#2A1240]">Packages, pricing & inventory are under NDA.</h3>
        {!user && <><p className="mt-3 text-slate-300">Sign in to request access.</p><button className="btn-gold mt-6" onClick={() => nav(`/login?next=/briefs/${b.id}`)} data-testid="nda-login-button">Sign in to request access</button></>}
        {user && !n && <><p className="mt-3 text-slate-300">{b.org_name} shares the full brief after a mutual NDA. Request access and they'll send it to you.</p>
          <button className="btn-gold mt-6" disabled={busy} onClick={() => run(() => api.post(`/briefs/${b.id}/nda/request`), "Access requested — you'll be alerted when the NDA arrives")} data-testid="nda-request-button"><FileSignature className="h-4 w-4" />Request access</button></>}
        {user && n?.status === "requested" && <p className="mt-4 rounded-full border border-[#2A1240]/15 inline-block px-4 py-2 text-sm text-slate-200" data-testid="nda-status-requested">Access requested · waiting for {b.org_name} to send the NDA</p>}
        {user && n?.status === "sent" && (
          <div className="mt-6" data-testid="nda-sign-form">
            <p className="kicker">Non-disclosure agreement</p>
            <div className="mt-3 max-h-48 overflow-y-auto rounded-xl border border-[#2A1240]/10 bg-[#FFF7FB] p-5 text-sm leading-relaxed text-slate-200 whitespace-pre-line" data-lenis-prevent data-testid="nda-text">{n.text}</div>
            <input className="field mt-4" placeholder="Type your full legal name to sign" value={sig} onChange={(e) => setSig(e.target.value)} data-testid="nda-signature-input" />
            {sig && <p className="mt-3 font-display italic text-3xl text-[#8E2BC4]" data-testid="nda-signature-preview">{sig}</p>}
            <label className="mt-4 flex items-start gap-3 text-sm text-slate-300"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 accent-[#E0218A]" data-testid="nda-agree-checkbox" />I agree to the NDA on behalf of my company and understand this is a legally binding electronic signature.</label>
            <button className="btn-gold mt-5" disabled={busy || sig.trim().length < 2 || !agree} onClick={() => run(() => api.post(`/ndas/${n.id}/sign`, { signature_name: sig, agree }), "Partnership details unlocked")} data-testid="nda-sign-button"><Unlock className="h-4 w-4" />Sign NDA & unlock</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Packages({ b, onChoose, mine }) {
  const pk = Array.isArray(b.packages) ? b.packages : [];
  const assets = [...new Set(pk.flatMap((p) => (p.items || []).map((i) => i.asset)))];
  if (!pk.length) return null;
  return (
    <section className="mt-24" data-testid="brief-packages">
      <p className="kicker">Partnership packages</p>
      <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[#2A1240]">Compare and choose your tier.</h2>
      <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {pk.map((p, i) => {
          const s = tierStyle(p.tier);
          const taken = b.taken?.[p.id];
          return (
            <motion.div key={p.id} initial={{ opacity: 1, y: 0 }} whileHover={{ y: -6 }} transition={{ duration: 0.5, ease: EASE }}
              className={`relative flex flex-col rounded-3xl border bg-[#FFFFFF] p-7 ${s.ring} ${i === 0 ? "lg:shadow-[0_30px_80px_-40px_rgba(224,33,138,0.5)]" : ""}`} data-testid={`package-card-${i}`}>
              <TierBadge tier={p.tier} className="text-sm uppercase tracking-[0.18em]" />
              <p className="mt-5 font-display text-4xl text-[#2A1240]" data-testid={`package-price-${i}`}>{p.price_label || `$${Number(p.price || 0).toLocaleString()}`}</p>
              <ul className="mt-6 space-y-2.5 text-sm text-slate-200 flex-1">
                {(p.items || []).map((it) => <li key={it.asset} className="flex gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: s.color }} /><span><span className="font-mono">{it.qty}×</span> {it.asset}</span></li>)}
                {(p.extras || []).map((x) => <li key={x} className="flex gap-2"><Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: s.color }} />{x}</li>)}
                {!p.items?.length && !p.extras?.length && <li className="text-slate-400">Benefits shared on request</li>}
              </ul>
              {!mine && <button className={`mt-7 ${i === 0 ? "btn-gold" : "btn-ghost"}`} disabled={taken} onClick={() => onChoose(p)} data-testid={`package-choose-${i}`}>{taken ? "Reserved" : `Choose ${p.tier}`}</button>}
            </motion.div>
          );
        })}
      </div>
      {assets.length > 0 && (
        <Reveal className="mt-10 overflow-x-auto rounded-2xl border border-[#2A1240]/10" data-testid="package-compare-table">
          <table className="w-full min-w-[640px] text-sm">
            <thead><tr className="bg-[#FFFFFF]"><th className="p-4 text-left text-[11px] uppercase tracking-[0.18em] text-slate-400 font-normal">Asset</th>{pk.map((p) => <th key={p.id} className="p-4 text-center"><TierBadge tier={p.tier} className="text-xs justify-center" /></th>)}</tr></thead>
            <tbody>
              {assets.map((a) => (
                <tr key={a} className="border-t border-[#2A1240]/10">
                  <td className="p-4 text-slate-200">{a}</td>
                  {pk.map((p) => { const it = (p.items || []).find((x) => x.asset === a); return <td key={p.id} className="p-4 text-center font-mono text-[#2A1240]">{it ? it.qty : <Minus className="h-4 w-4 mx-auto text-slate-500" />}</td>; })}
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>
      )}
    </section>
  );
}

const Inventory = ({ items }) => items?.length ? (
  <section className="mt-24" data-testid="brief-inventory">
    <p className="kicker">Sponsorship inventory</p>
    <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[#2A1240]">What's still available.</h2>
    <div className="mt-8 divide-y divide-[#2A1240]/10 border-y border-[#2A1240]/10">
      {items.map((i) => (
        <div key={i.id} className="flex items-center justify-between gap-4 py-4" data-testid={`inventory-row-${i.id}`}>
          <span className="text-[#2A1240]">{i.asset}</span>
          <div className="flex items-center gap-4">
            <div className="hidden sm:block h-1.5 w-40 rounded-full bg-[#2A1240]/10 overflow-hidden"><div className="h-full bg-[#E0218A]" style={{ width: `${i.total ? (i.available / i.total) * 100 : 0}%` }} /></div>
            <span className={`font-mono text-sm ${i.available ? "text-[#8E2BC4]" : "text-slate-500 line-through"}`}>{i.available} / {i.total}</span>
          </div>
        </div>
      ))}
    </div>
  </section>
) : null;

export default function Brief() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [err, setErr] = useState("");
  useSeo(b?.title ? (/brief/i.test(b.title) ? b.title : `${b.title} Partnership Brief`) : "Partnership Brief", b?.title ? `${b.org_name || "A partner"} is looking for partners for ${b.title}. See packages, deliverables and deadlines on Balqis Book.` : "Partnership brief on Balqis Book.", `/briefs/${id}`);
  const [pick, setPick] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/briefs/${id}`).then((r) => setB(r.data)).catch((e) => setErr(errMsg(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const mine = user && b && user.company_id === b.company_id;
  const choose = (p) => {
    if (!user) { nav(`/login?next=/briefs/${id}`); return; }
    setPick(p);
    setMsg(`Hi ${b.org_name}, we'd like the ${p.tier} package for ${b.title}.`);
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/briefs/${id}/choose`, { package_id: pick.id, message: msg });
      toast.success(`${pick.tier} package requested — added to your pipeline`);
      nav(`/app/messages?thread=${data.thread_id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen bg-[#FFF7FB]"><Nav /><p className="pt-40 text-center text-slate-300" data-testid="brief-error">{err}</p></div>;
  if (!b) return <div className="min-h-screen bg-[#FFF7FB]"><Nav /><p className="pt-40 text-center text-slate-400">Loading brief…</p></div>;

  return (
    <div className="min-h-screen bg-[#FFF7FB]" data-testid="brief-page">
      <Nav />
      <section className="relative pt-16">
        <div className="relative h-[46vh] min-h-[320px] overflow-hidden" style={{ clipPath: "inset(0 0 0 0)" }}>
          <motion.img initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 2.2, ease: EASE }} src={b.cover_image || "/img/gala.jpg"} alt={b.title} className="h-full w-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FFF7FB] via-[#FFF7FB]/60 to-transparent" />
        </div>
        <div className="relative -mt-56 mx-auto max-w-7xl px-5 sm:px-8">
          <p className="text-xs sm:text-sm uppercase tracking-[0.42em] text-[#8E2BC4]" data-testid="brief-org">{b.org_name}</p>
          {b.sample && <p className="mt-4 flex flex-wrap items-center gap-2 text-sm text-[#92400E]" data-testid="brief-sample-note"><SampleBadge show testid="brief-page-sample" />Example brief to show how Balqis Book works. Not a real company.</p>}
          <MaskedLines as="h1" testid="brief-title" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-[#2A1240] tracking-tight" lines={[b.title]} />
          <div className="mt-4"><WinnerBadges companyId={b.company_id} testid="brief-winner-badges" /></div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <DeadlineChip date={b.deadline} testid="brief-deadline-chip" />
            {b.unlocked_by_nda && <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 px-3 py-1 text-xs text-emerald-300" data-testid="brief-unlocked-badge"><Unlock className="h-3.5 w-3.5" />Partnership details unlocked</span>}
            <button className="btn-ghost !py-1.5 !px-3 !text-xs" onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Brief link copied"); }} data-testid="brief-copy-link"><Link2 className="h-3.5 w-3.5" />Share</button>
            <button className="btn-ghost !py-1.5 !px-3 !text-xs" onClick={() => window.print()} data-testid="brief-print"><Printer className="h-3.5 w-3.5" />Print / PDF</button>
            {mine && <Link to="/app/briefs" className="btn-gold !py-1.5 !px-3 !text-xs" data-testid="brief-manage-link">Manage brief</Link>}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24">
        <div className="mt-16 grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="brief-facts">
          <Fact label="Looking for" value={b.looking_for} testid="brief-looking-for" />
          <Fact label="Target audience" value={b.target_audience} testid="brief-audience" />
          <Fact label="Expected reach" value={b.expected_reach} testid="brief-reach" />
          <Fact label="Location" value={b.location} testid="brief-location" />
          <Fact label="Partnership value" value={b.locked ? "Under NDA" : b.value_range} testid="brief-value" />
          <Fact label="Activation" value={b.activation_start ? `${fmtDate(b.activation_start)}${b.activation_end ? ` – ${fmtDate(b.activation_end)}` : ""}` : ""} testid="brief-activation" />
        </div>
        {b.assets?.length > 0 && (
          <Reveal className="mt-16">
            <p className="kicker">Available assets</p>
            <div className="mt-5 flex flex-wrap gap-3" data-testid="brief-assets">
              {b.assets.map((a) => <span key={a} className="rounded-full border border-[#E0218A]/30 bg-[#E0218A]/5 px-5 py-2.5 text-sm text-[#2A1240]">{a}</span>)}
            </div>
          </Reveal>
        )}
        {b.overview && <Reveal className="mt-16 max-w-3xl"><p className="text-lg leading-relaxed text-slate-200 whitespace-pre-line" data-testid="brief-overview">{b.overview}</p></Reveal>}
        {b.confidential_notes && !b.locked && <div className="mt-10 max-w-3xl rounded-2xl border border-[#E0218A]/30 bg-[#E0218A]/5 p-6" data-testid="brief-confidential"><p className="kicker flex items-center gap-2"><Unlock className="h-3.5 w-3.5" />Confidential</p><p className="mt-3 text-slate-200 whitespace-pre-line">{b.confidential_notes}</p></div>}
        {b.locked ? <div className="mt-20"><NdaGate b={b} reload={load} /></div> : (
          <>
            <Packages b={b} onChoose={choose} mine={mine} />
            <Inventory items={b.inventory} />
          </>
        )}
      </section>
      <Dialog open={!!pick} onOpenChange={(o) => !o && setPick(null)}>
        <DialogContent className="bg-[#FFFFFF] border-[#2A1240]/10" data-testid="choose-package-dialog">
          <DialogTitle className="font-display text-2xl font-normal text-[#2A1240]">Choose {pick?.tier}</DialogTitle>
          <DialogDescription className="text-slate-400">{pick?.price_label} · sent as a proposal to {b.org_name} and added to your pipeline.</DialogDescription>
          <textarea rows={4} className="field" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="choose-package-message" />
          <button className="btn-gold" disabled={busy} onClick={confirm} data-testid="choose-package-confirm">{busy ? "Sending…" : "Send package request"}</button>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
